/**
 * Assets Logo — manual upload of company logo SVGs to the shared
 * `trfrontendstorage` `$web` container, served via Fastly at
 * https://tr-cdn.tipranks.com/static/v2/static/logos/<TICKER>.svg
 *
 * Uploads NEVER overwrite: if <TICKER>.svg already exists the upload is
 * rejected (enforced atomically server-side via an If-None-Match:* condition).
 */

/** Folder inside the `$web` container where logos live. No trailing slash. */
export const ASSET_LOGO_BLOB_PREFIX = "static/v2/static/logos";

/** Public CDN base (Fastly) for logos. No trailing slash. */
export const ASSET_LOGO_CDN_BASE =
  "https://tr-cdn.tipranks.com/static/v2/static/logos";

/**
 * Allowed ticker characters: uppercase letters, digits, and the separators
 * seen in real tickers — colon (exchange prefix, e.g. GB:AAPL), dot (BRK.B),
 * hyphen and underscore. Case is enforced (uppercase only).
 */
export const ASSET_LOGO_TICKER_PATTERN = /^[A-Z0-9.:_-]+$/;

export function isValidLogoTicker(ticker: string): boolean {
  const t = ticker.trim();
  return t.length > 0 && t.length <= 30 && ASSET_LOGO_TICKER_PATTERN.test(t);
}

/** `AAPL` → `static/v2/static/logos/AAPL.svg` */
export function logoBlobName(ticker: string): string {
  return `${ASSET_LOGO_BLOB_PREFIX}/${ticker}.svg`;
}

/** `AAPL` → `https://tr-cdn.tipranks.com/static/v2/static/logos/AAPL.svg` */
export function logoPublicUrl(ticker: string): string {
  return `${ASSET_LOGO_CDN_BASE}/${encodeURIComponent(`${ticker}.svg`)}`;
}

export interface AssetLogoExistsResponse {
  ticker: string;
  blobName: string;
  url: string;
  exists: boolean;
}

export interface UploadAssetLogoRequest {
  /** Ticker (uppercase, may contain a colon). The `.svg` is added server-side. */
  ticker: string;
  /** Raw SVG markup (SVGs are text, so they travel as a JSON string). */
  svg: string;
}

export interface UploadAssetLogoResponse {
  ticker: string;
  blobName: string;
  url: string;
}
